import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { hexToHsv, hsvToHex, isHex, toHex, type Hsv } from './tone';
import './color.css';

interface Props {
  /** Aktuelle Farbe: `#rrggbb` oder der Name einer Vorgabe (ältere Einträge) */
  value: string;
  onChange: (hex: string) => void;
  label: string;
}

const clamp = (n: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, n));

/**
 * Freie Farbwahl mit der RGB-Farbpalette: Auf der Fläche wählt man Sättigung (nach rechts) und Helligkeit (nach oben), mit dem Regler darunter den Farbton. Das Feld daneben
 * zeigt den Farbwert und nimmt einen eigenen entgegen (`#1a2b3c`). Bedienbar per Maus, Finger und Tastatur (Pfeiltasten auf der Fläche, Pfeile am Regler). Das Ergebnis ist
 * immer `#rrggbb`.
 */
export function ColorPicker({ value, onChange, label }: Props) {
  const hex = toHex(value);
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(hex));
  const [text, setText] = useState(hex);
  const pad = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  // Kommt die Farbe von außen (anderes Feld, Zurücksetzen), folgt die Palette. Der Farbton bleibt bei Grautönen erhalten, sonst spränge der Regler.
  useEffect(() => {
    if (hsvToHex(hsv) !== hex) setHsv(hexToHsv(hex));
    setText(hex);
  }, [hex]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (next: Hsv) => {
    setHsv(next);
    const out = hsvToHex(next);
    setText(out);
    onChange(out);
  };

  const fromPointer = (e: PointerEvent<HTMLDivElement>) => {
    const rect = pad.current!.getBoundingClientRect();
    apply({ ...hsv, s: clamp((e.clientX - rect.left) / rect.width), v: clamp(1 - (e.clientY - rect.top) / rect.height) });
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 0.1 : 0.02;
    const move: Record<string, Partial<Hsv>> = {
      ArrowLeft: { s: clamp(hsv.s - step) },
      ArrowRight: { s: clamp(hsv.s + step) },
      ArrowUp: { v: clamp(hsv.v + step) },
      ArrowDown: { v: clamp(hsv.v - step) },
    };
    if (!move[e.key]) return;
    e.preventDefault();
    apply({ ...hsv, ...move[e.key] });
  };

  const commitText = () => {
    const raw = text.trim().toLowerCase();
    const full = /^#?[0-9a-f]{3}$/.test(raw) ? `#${raw.replace('#', '').split('').map((c) => c + c).join('')}` : raw.startsWith('#') ? raw : `#${raw}`;
    if (isHex(full)) {
      setHsv(hexToHsv(full));
      setText(full);
      onChange(full);
    } else {
      setText(hex);
    }
  };

  const hue = `hsl(${Math.round(hsv.h)}, 100%, 50%)`;
  return (
    <div className="cpick" role="group" aria-label={label}>
      <div
        ref={pad}
        className="cpick__pad"
        style={{ backgroundColor: hue }}
        tabIndex={0}
        role="slider"
        aria-label="Sättigung und Helligkeit"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(hsv.s * 100)}
        aria-valuetext={`Sättigung ${Math.round(hsv.s * 100)} Prozent, Helligkeit ${Math.round(hsv.v * 100)} Prozent`}
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          fromPointer(e);
        }}
        onPointerMove={(e) => dragging.current && fromPointer(e)}
        onPointerUp={() => (dragging.current = false)}
        onPointerCancel={() => (dragging.current = false)}
        onKeyDown={onKey}
      >
        <span className="cpick__thumb" style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, backgroundColor: hex }} aria-hidden="true" />
      </div>
      <input
        className="cpick__hue"
        type="range"
        min={0}
        max={359}
        step={1}
        value={Math.round(hsv.h) % 360}
        aria-label="Farbton"
        onChange={(e) => apply({ ...hsv, h: Number(e.target.value) })}
      />
      <div className="cpick__value">
        <span className="cpick__preview" style={{ backgroundColor: hex }} aria-hidden="true" />
        <label className="cpick__hex">
          <span className="sr-only">Farbwert als Hexadezimalzahl</span>
          <input
            className="input"
            value={text}
            maxLength={7}
            spellCheck={false}
            autoComplete="off"
            inputMode="text"
            onChange={(e) => setText(e.target.value)}
            onBlur={commitText}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), commitText())}
          />
        </label>
      </div>
    </div>
  );
}
